import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { request } from '../shared/request';

const MAX_ATTACHMENTS = 3;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'application/pdf'];

const signFile = (fileName, contentType, sitekey) => (
  window.grecaptcha.enterprise.execute(sitekey, { action: 'trade' }).then((token) => (
    request('/trade_program_requests/sign', {
      method: 'POST',
      body: JSON.stringify({ file_name: fileName, content_type: contentType, captcha: token })
    })
  ))
);

const TradeAttachments = ({ sitekey, formSelector }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const hiddenInputs = useRef({});
  const xhrs = useRef({});
  // ids that no longer occupy a slot (removed by the user or failed to upload).
  // Guards late sign/xhr callbacks so a removed file can't append a hidden input.
  const released = useRef({});
  // Synchronous count of occupied slots. Used for admission so rapid consecutive
  // selections can't over-admit past MAX_ATTACHMENTS before React re-renders.
  const slots = useRef(0);
  const idSeq = useRef(0);

  const updateItem = (id, attrs) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...attrs } : it)));
  };

  const addHiddenInput = (id, key) => {
    const form = document.querySelector(formSelector);
    if (!form) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'trade_program_request[attachment_keys][]';
    input.value = key;
    form.appendChild(input);
    hiddenInputs.current[id] = input;
  };

  const removeHiddenInput = (id) => {
    const input = hiddenInputs.current[id];
    if (input && input.parentNode) {
      input.parentNode.removeChild(input);
    }
    delete hiddenInputs.current[id];
  };

  // Release the slot an attachment holds, at most once. Called when an upload
  // fails or its row is removed, so a failure never permanently steals a slot.
  const releaseSlot = (id) => {
    if (released.current[id]) {
      return;
    }
    released.current[id] = true;
    slots.current = Math.max(0, slots.current - 1);
  };

  const uploadFile = (id, file) => {
    signFile(file.name, file.type, sitekey).then(({ url, newHref, values }) => {
      if (released.current[id]) {
        return;
      }
      const formData = new FormData();
      Object.entries(values).forEach(([k, v]) => {
        if (k === 'contentType') {
          formData.append('Content-Type', v);
        } else {
          formData.append(k, v);
        }
      });
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhrs.current[id] = xhr;
      xhr.open('POST', url, true);

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          updateItem(id, { progress: percentComplete.toFixed(0) });
        }
      });

      xhr.onload = () => {
        delete xhrs.current[id];
        if (released.current[id]) {
          return;
        }
        if (xhr.status === 204) {
          addHiddenInput(id, values.key);
          updateItem(id, { status: 'done', href: newHref });
        } else {
          releaseSlot(id);
          updateItem(id, { status: 'error', message: 'Upload failed' });
        }
      };

      xhr.onerror = () => {
        delete xhrs.current[id];
        if (released.current[id]) {
          return;
        }
        releaseSlot(id);
        updateItem(id, { status: 'error', message: 'Upload failed' });
      };

      xhr.send(formData);
    }).catch(() => {
      if (released.current[id]) {
        return;
      }
      releaseSlot(id);
      updateItem(id, { status: 'error', message: 'Could not prepare upload' });
    });
  };

  const onChange = (event) => {
    const input = event.currentTarget;
    const selected = Array.from(input.files || []);
    input.value = '';

    let message = '';
    const accepted = [];
    selected.forEach((file) => {
      if (slots.current >= MAX_ATTACHMENTS) {
        message = `You can attach up to ${ MAX_ATTACHMENTS } files.`;
      } else if (!ACCEPTED.includes(file.type)) {
        message = 'Only JPEG, PNG, or PDF files are allowed.';
      } else if (file.size > MAX_BYTES) {
        message = 'Each file must be 10MB or smaller.';
      } else {
        const id = `${ idSeq.current }`;
        idSeq.current += 1;
        slots.current += 1;
        accepted.push({ id, file });
      }
    });
    setError(message);

    accepted.forEach(({ id, file }) => {
      setItems((prev) => [...prev, {
        id, name: file.name, status: 'uploading', progress: 0
      }]);
      uploadFile(id, file);
    });
  };

  const onRemove = (id) => {
    const xhr = xhrs.current[id];
    if (xhr) {
      xhr.abort();
      delete xhrs.current[id];
    }
    releaseSlot(id);
    removeHiddenInput(id);
    setItems((prev) => prev.filter((it) => it.id !== id));
    setError('');
  };

  return (
    <div className="trade-attachments">
      <ul className="trade-attachments-list">
        { items.map((it) => (
          <li key={ it.id } className={ `trade-attachment trade-attachment-${ it.status }` }>
            <span className="trade-attachment-name">{ it.name }</span>
            { it.status === 'uploading' && (
            <span className="trade-attachment-status">
              {' '}
              — uploading
              {' '}
              { it.progress }
              %
            </span>
            ) }
            { it.status === 'done' && <span className="trade-attachment-status"> — ready</span> }
            { it.status === 'error' && (
            <span className="trade-attachment-status">
              {' '}
              —
              { it.message }
            </span>
            ) }
            <button type="button" className="trade-attachment-remove" onClick={ () => onRemove(it.id) }>Remove</button>
          </li>
        )) }
      </ul>
      { error && <div className="trade-attachments-error">{ error }</div> }
      { items.filter((it) => it.status !== 'error').length < MAX_ATTACHMENTS && (
        <input
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          multiple
          onChange={ onChange }
        />
      ) }
    </div>
  );
};

TradeAttachments.propTypes = {
  sitekey: PropTypes.string.isRequired,
  formSelector: PropTypes.string.isRequired
};

export default TradeAttachments;
